import { useState, Fragment, useMemo } from 'react';
import { Button, Grid2 as Grid, Typography, Drawer, IconButton, Stack } from '@mui/material';
import { withStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form-v7';
import { COLORS } from '../../../utils/Application_Constants';
import FilterAltIcon from '../../../assets/icons/FilterAltIcon';
import { FILTER_TYPES, countTotalNumFilters, getDefaultValues } from '../../../utils/filterUtils';
import CollapsibleDateRange from './Inputs/CollapsibleDateRange';
import CollapsibleCheckbox from './Inputs/CollapsibleCheckbox';
import CheckboxMenu from './Inputs/CheckboxMenu';

const styles = () => ({
    filterButton: {
        color: COLORS.LT_JOURNEY,
        background: COLORS.LT_JOURNEY10,
        height: '34px',
    },
    actions: { padding: '1.5em 2em' },
    header: {
        fontSize: '1.5em',
        color: COLORS.DARK_BLUE,
        padding: '2em 2rem 0.5em',
    },
    exit: {
        position: 'absolute',
        top: '0.75em',
        right: '0.75em',
    },
    formWrapper: {
        height: '100vh',
        maxHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
    },
    container: {
        paddingLeft: '2em',
        paddingRight: '2em',
        overflowY: 'scroll',
    },
});

/**
 * FilterDrawer Component
 * A reusable drawer component for managing and displaying filters
 *
 * @component
 * @param {Object} props
 * @param {Object} props.classes - Material-UI classes object
 * @param {boolean} [props.isLoading=false] - Loading state of the filter drawer
 * @param {Array<FilterConfig>} [props.config=[]] - Array of filter configurations
 * @param {Function} props.onChange - Callback function when filters change
 * @param {Object} [props.storedValues] - Previously stored filter values
 *
 * @typedef {Object} FilterConfig
 * @property {string} name - Unique identifier for the filter
 * @property {string} label - Display name for the filter
 * @property {('checkbox'|'date-range')} type - Type of filter
 * @property {*} defaultValue - Default value for the filter
 * @property {boolean} [isQuickFilter] - Whether to show in quick filters section
 * @property {Object} [overrides] - Override options for the filter
 *
 * Filter Types:
 *
 * 1. Checkbox Filter:
 * @typedef {Object} CheckboxFilterConfig
 * @property {string} name - Unique identifier
 * @property {string} label - Display name
 * @property {'checkbox'} type - Must be 'checkbox'
 * @property {string[]} defaultValue - Array of selected values
 * @property {boolean} [isQuickFilter] - Show in quick filters
 * @property {Object} [overrides] - Override options
 * @property {Array<string|{label: string, value: string}>} [overrides.options] - Available options
 *
 * 2. Date Range Filter:
 * @typedef {Object} DateRangeFilterConfig
 * @property {string} name - Unique identifier
 * @property {string} label - Display name
 * @property {'date-range'} type - Must be 'date-range'
 * @property {Object} defaultValue - Default date range
 * @property {Date|null} defaultValue.from - Start date
 * @property {Date|null} defaultValue.to - End date
 *
 * @example
 * // Basic usage with checkbox filter
 * <FiltersDrawer
 *   config={[
 *     {
 *       name: 'status',
 *       label: 'Status',
 *       type: 'checkbox',
 *       defaultValue: [],
 *       isQuickFilter: true,
 *       overrides: {
 *         options: ['Active', 'Pending', 'Completed']
 *       }
 *     }
 *   ]}
 *   onChange={(filters) => console.log(filters)}
 * />
 *
 * @example
 * // Date range filter
 * <FiltersDrawer
 *   config={[
 *     {
 *       name: 'dateRange',
 *       label: 'Date Range',
 *       type: 'date-range',
 *       defaultValue: { from: null, to: null }
 *     }
 *   ]}
 *   onChange={(filters) => console.log(filters)}
 * />
 *
 * @example
 * // Complex configuration with multiple filters
 * <FiltersDrawer
 *   config={[
 *     {
 *       name: 'status',
 *       label: 'Status',
 *       type: 'checkbox',
 *       defaultValue: [],
 *       isQuickFilter: true,
 *       overrides: {
 *         options: ['Active', 'Pending', 'Completed']
 *       }
 *     },
 *     {
 *       name: 'dateRange',
 *       label: 'Date Range',
 *       type: 'date-range',
 *       defaultValue: { from: null, to: null }
 *     },
 *     {
 *       name: 'categories',
 *       label: 'Categories',
 *       type: 'checkbox',
 *       defaultValue: [],
 *       overrides: {
 *         options: [
 *           { label: 'Category 1', value: 'cat1' },
 *           { label: 'Category 2', value: 'cat2' }
 *         ]
 *       }
 *     }
 *   ]}
 *   onChange={(filters) => console.log(filters)}
 *   storedValues={{
 *     status: ['Active'],
 *     dateRange: { from: new Date(), to: null }
 *   }}
 * />
 */
const FiltersDrawer = ({ classes, isLoading = false, config = [], onChange, storedValues }) => {
    const defaultValues = getDefaultValues(config);
    const { control, handleSubmit, watch, reset } = useForm({
        defaultValues: storedValues ?? defaultValues,
    });
    const allFormValues = watch();
    const [lastAppliedFilterCount, setLastAppliedFilterCount] = useState(() =>
        countTotalNumFilters(allFormValues)
    );

    const [drawerOpen, setDrawerOpen] = useState(false);
    const toggleDrawer = () => setDrawerOpen((prevValue) => !prevValue);

    // Reset the filter to it's default values (the spread operator is required instead of passing the object reference)
    const handleResetToDefaultValues = () => reset({ ...defaultValues });
    // Reset the filter to it's previous stored values
    const handleResetToPreviousValues = () => reset(storedValues ?? defaultValues);

    const handleFilterApply = (data) => {
        setLastAppliedFilterCount(countTotalNumFilters(data));
        onChange(data);
        reset(data);
    };

    const handleApplyDrawerFilters = (data) => {
        handleFilterApply(data);
        toggleDrawer();
    };
    const handleCloseDrawer = () => {
        handleResetToPreviousValues();
        toggleDrawer();
    };

    const quickFilters = useMemo(() => config.filter((filter) => filter.isQuickFilter), [config]);

    return (
        <>
            <Stack direction="row" spacing={2} alignItems="center">
                <Button
                    loading={isLoading}
                    onClick={toggleDrawer}
                    className={classes.filterButton}
                    data-testid="filters-button"
                >
                    <FilterAltIcon />
                    Filters ({lastAppliedFilterCount})
                </Button>
                <Stack direction="row" spacing={2} alignItems="center" ml="20px">
                    {quickFilters.map((filter) =>
                        filter.type === FILTER_TYPES.CHECKBOX ? (
                            <CheckboxMenu
                                key={filter.name}
                                control={control}
                                name={filter.name}
                                label={filter.label || filter.name}
                                defaultValue={filter.defaultValue}
                                options={filter.overrides?.options || []}
                                render={filter.overrides?.render}
                                onApply={handleSubmit(handleFilterApply)}
                                onReset={handleResetToPreviousValues}
                            >
                                {filter.label}
                            </CheckboxMenu>
                        ) : null
                    )}
                </Stack>
            </Stack>
            <Drawer open={drawerOpen} onClose={handleCloseDrawer} sx={{ zIndex: 1310 }}>
                <form
                    data-testid="filters-form"
                    onSubmit={handleSubmit(handleApplyDrawerFilters)}
                    className={classes.formWrapper}
                >
                    <Grid>
                        <Typography variant="h5" component="h5" className={classes.header}>
                            Filters ({countTotalNumFilters(allFormValues)})
                        </Typography>
                        <IconButton onClick={handleCloseDrawer} className={classes.exit}>
                            <CloseIcon
                                sx={{ fontSize: '1.15em' }}
                                data-testid="close-filters-button"
                            />
                        </IconButton>
                    </Grid>
                    <Grid container spacing={1} className={classes.actions}>
                        <Button
                            variant="contained"
                            color="grey"
                            onClick={handleResetToDefaultValues}
                        >
                            Reset All
                        </Button>
                        <Button type="submit" variant="contained" color="primary">
                            Apply Filters
                        </Button>
                    </Grid>
                    <Grid className={classes.container}>
                        {config.map((configItem) => (
                            <Fragment key={configItem.name}>
                                {configItem.type === FILTER_TYPES.CHECKBOX ? (
                                    <CollapsibleCheckbox
                                        control={control}
                                        name={configItem.name}
                                        label={configItem.label || configItem.name}
                                        defaultValue={configItem.defaultValue}
                                        options={configItem.overrides?.options || []}
                                        render={configItem.overrides?.render}
                                        emptyLabel={configItem.overrides?.emptyLabel}
                                    />
                                ) : (
                                    <CollapsibleDateRange
                                        control={control}
                                        name={configItem.name}
                                        label={configItem.label}
                                    />
                                )}
                            </Fragment>
                        ))}
                    </Grid>
                </form>
            </Drawer>
        </>
    );
};

export default withStyles(styles)(FiltersDrawer);
